import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Compass, Navigation, MapPin, Clock, Route, ChevronRight, X } from 'lucide-react';
import { getClubStatus, getOpeningTimeToday, STATUS_CONFIG } from '../utils/clubStatus';
import { useLanguage } from '../contexts/LanguageContext';

// Radius options - consistent across the app
export const RADIUS_STEPS = [10, 25, 50, 100, 150, 200];
export const DEFAULT_RADIUS = 50;
export const DEFAULT_RADIUS_INDEX = 2; // Index of 50km in RADIUS_STEPS

// Calculate distance between two coordinates (Haversine formula)
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Hook to manage user location.
// First call to `requestLocation()` does a one-shot getCurrentPosition,
// AND kicks off a long-running watchPosition so the cached value
// continuously updates as the user moves. This is what makes the
// motorcycle marker actually follow the user on the live map.
export const useUserLocation = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const watchIdRef = useRef(null);

  // Tear down the watcher when the hook unmounts so we don't leak GPS
  // listeners across page navigations.
  useEffect(() => {
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const startWatch = useCallback(() => {
    if (watchIdRef.current != null) return;
    if (!navigator.geolocation) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation([latitude, longitude]);
      },
      () => { /* silent: kept cached value */ },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
  }, []);

  const requestLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        setLocationError('GEO_NOT_SUPPORTED');
        reject(new Error('Geolocation not supported'));
        return;
      }

      setLocationLoading(true);
      setLocationError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          setLocationLoading(false);
          startWatch();
          resolve([latitude, longitude]);
        },
        (error) => {
          setLocationLoading(false);
          // Store error codes so consumer can translate:
          // 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT'
          const errorCode = error.code === error.PERMISSION_DENIED ? 'PERMISSION_DENIED' : 'POSITION_UNAVAILABLE';
          setLocationError(errorCode);
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }, [startWatch]);

  return { userLocation, locationLoading, locationError, requestLocation, setUserLocation };
};

// Hook to filter clubs by radius
export const useRadiusFilter = (clubs, userLocation, radius, enabled = true) => {
  return useMemo(() => {
    if (!enabled || !userLocation || !clubs?.length) {
      return clubs || [];
    }

    return clubs
      .map(club => ({
        ...club,
        distance: calculateDistance(
          userLocation[0], userLocation[1],
          club.latitude, club.longitude
        )
      }))
      .filter(club => club.distance <= radius)
      .sort((a, b) => a.distance - b.distance);
  }, [clubs, userLocation, radius, enabled]);
};

// ===== RADIUS SLIDER - ABOVE MAP VERSION =====
// Clean, responsive design - placed ABOVE the map
export const RadiusSliderAboveMap = ({
  radiusIndex,
  onRadiusIndexChange,
  currentRadius,
  onClose,
  nearestClubInfo,
  onSelectClub,
}) => {
  const { t } = useLanguage();
  return (
    <div className="mb-4">
      <div className="bg-zinc-800/90 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
        {/* Header with close button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-emerald-400" />
            <span className="text-cream font-medium text-sm">{t('map.searchRadius')}</span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center transition-colors"
            data-testid="disable-radius-btn"
          >
            <X className="w-4 h-4 text-cream-muted" />
          </button>
        </div>
        
        {/* Slider section */}
        <div className="px-4 py-4">
          {/* Current value display */}
          <div className="text-center mb-4">
            <span className="text-emerald-400 font-heading text-3xl font-bold">
              {currentRadius}
            </span>
            <span className="text-emerald-400/70 text-lg ml-1">km</span>
          </div>
          
          {/* Slider track */}
          <div className="relative px-2">
            <input
              type="range"
              min="0"
              max={RADIUS_STEPS.length - 1}
              value={radiusIndex}
              onChange={(e) => onRadiusIndexChange(parseInt(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                bg-zinc-600
                [&::-webkit-slider-runnable-track]:h-1.5
                [&::-webkit-slider-runnable-track]:bg-gradient-to-r
                [&::-webkit-slider-runnable-track]:from-emerald-600
                [&::-webkit-slider-runnable-track]:to-emerald-400
                [&::-webkit-slider-runnable-track]:rounded-full
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-6
                [&::-webkit-slider-thumb]:h-7
                [&::-webkit-slider-thumb]:mt-[-11px]
                [&::-webkit-slider-thumb]:rounded-[50%_50%_50%_50%_/_35%_35%_65%_65%]
                [&::-webkit-slider-thumb]:bg-gradient-to-b
                [&::-webkit-slider-thumb]:from-emerald-300
                [&::-webkit-slider-thumb]:to-emerald-500
                [&::-webkit-slider-thumb]:border-2
                [&::-webkit-slider-thumb]:border-white/30
                [&::-webkit-slider-thumb]:shadow-lg
                [&::-webkit-slider-thumb]:shadow-emerald-900/50
                [&::-webkit-slider-thumb]:cursor-grab
                [&::-webkit-slider-thumb]:active:cursor-grabbing
                [&::-webkit-slider-thumb]:hover:scale-110
                [&::-webkit-slider-thumb]:transition-transform
                [&::-moz-range-track]:h-1.5
                [&::-moz-range-track]:bg-gradient-to-r
                [&::-moz-range-track]:from-emerald-600
                [&::-moz-range-track]:to-emerald-400
                [&::-moz-range-track]:rounded-full
                [&::-moz-range-thumb]:w-6
                [&::-moz-range-thumb]:h-7
                [&::-moz-range-thumb]:rounded-[50%_50%_50%_50%_/_35%_35%_65%_65%]
                [&::-moz-range-thumb]:bg-gradient-to-b
                [&::-moz-range-thumb]:from-emerald-300
                [&::-moz-range-thumb]:to-emerald-500
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:cursor-grab"
              data-testid="radius-slider"
            />
          </div>
          
          {/* Kilometer marks */}
          <div className="flex justify-between mt-3 px-1">
            {RADIUS_STEPS.map((step, idx) => (
              <button 
                key={step}
                onClick={() => onRadiusIndexChange(idx)}
                className={`text-xs sm:text-sm px-1 py-0.5 rounded transition-all ${
                  idx === radiusIndex 
                    ? 'text-emerald-400 font-bold scale-110' 
                    : 'text-cream-muted hover:text-cream'
                }`}
              >
                {step}
              </button>
            ))}
          </div>
        </div>
        
        {/* Nearest Club Card */}
        {nearestClubInfo && (
          <div 
            className={`mx-4 mb-4 p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.01] ${
              nearestClubInfo.status === 'open' 
                ? 'bg-green-500/15 border border-green-500/30' 
                : nearestClubInfo.status === 'opens_later'
                  ? 'bg-yellow-500/15 border border-yellow-500/30'
                  : 'bg-red-500/15 border border-red-500/30'
            }`}
            onClick={() => onSelectClub?.(nearestClubInfo.club)}
            data-testid="nearest-club-card"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                nearestClubInfo.status === 'open' 
                  ? 'bg-green-500' 
                  : nearestClubInfo.status === 'opens_later'
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
              }`}>
                <Navigation className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium ${
                  nearestClubInfo.status === 'open' 
                    ? 'text-green-400' 
                    : nearestClubInfo.status === 'opens_later'
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }`}>
                  {nearestClubInfo.message}
                </div>
                <div className="font-heading text-cream truncate">
                  {nearestClubInfo.club.name}
                </div>
                <div className="text-cream-muted text-xs">
                  {nearestClubInfo.club.distance?.toFixed(1).replace('.', ',')} km • {nearestClubInfo.club.city || nearestClubInfo.club.region}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-cream-muted flex-shrink-0" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== PERMANENT RADIUS SLIDER =====
// Always visible, elegant thin green line with water-drop handle
export const PermanentRadiusSlider = ({
  radiusIndex,
  onRadiusIndexChange,
  currentRadius,
  userLocation,
  locationLoading,
  onRequestLocation,
  compact = false,
  className = '',
  t // Translation function - optional for backwards compatibility
}) => {
  // Use translation function or fallback to default text
  const getLabel = (key, fallback) => t ? t(key) : fallback;
  
  // Request location when slider is interacted with (if not already available)
  const handleSliderChange = async (e) => {
    const newIndex = parseInt(e.target.value);
    
    // Request location if not already available
    if (!userLocation && onRequestLocation) {
      try {
        await onRequestLocation();
      } catch (err) {
        // Location denied - still update the slider
      }
    }
    
    onRadiusIndexChange(newIndex);
  };

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="radius-slider-container">
      {/* Location indicator */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {locationLoading ? (
          <div className="w-5 h-5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
        ) : userLocation ? (
          <Compass className="w-5 h-5 text-emerald-400" />
        ) : (
          <Compass className="w-5 h-5 text-zinc-500" />
        )}
        {!compact && (
          <span className="text-sm text-cream-muted hidden sm:inline">{getLabel('map.radius', 'Radius')}</span>
        )}
      </div>
      
      {/* Slider - larger and more prominent */}
      <div className="flex-1 min-w-[100px] max-w-[250px]">
        <input
          type="range"
          min="0"
          max={RADIUS_STEPS.length - 1}
          value={radiusIndex}
          onChange={handleSliderChange}
          className="w-full h-2 rounded-full appearance-none cursor-pointer
            bg-zinc-700
            [&::-webkit-slider-runnable-track]:h-2
            [&::-webkit-slider-runnable-track]:bg-gradient-to-r
            [&::-webkit-slider-runnable-track]:from-emerald-600
            [&::-webkit-slider-runnable-track]:to-emerald-400
            [&::-webkit-slider-runnable-track]:rounded-full
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-5
            [&::-webkit-slider-thumb]:h-6
            [&::-webkit-slider-thumb]:mt-[-8px]
            [&::-webkit-slider-thumb]:rounded-[50%_50%_50%_50%_/_35%_35%_65%_65%]
            [&::-webkit-slider-thumb]:bg-gradient-to-b
            [&::-webkit-slider-thumb]:from-emerald-300
            [&::-webkit-slider-thumb]:to-emerald-500
            [&::-webkit-slider-thumb]:border-2
            [&::-webkit-slider-thumb]:border-white/40
            [&::-webkit-slider-thumb]:shadow-lg
            [&::-webkit-slider-thumb]:shadow-emerald-900/40
            [&::-webkit-slider-thumb]:cursor-grab
            [&::-webkit-slider-thumb]:active:cursor-grabbing
            [&::-webkit-slider-thumb]:hover:scale-110
            [&::-webkit-slider-thumb]:transition-transform
            [&::-moz-range-track]:h-2
            [&::-moz-range-track]:bg-gradient-to-r
            [&::-moz-range-track]:from-emerald-600
            [&::-moz-range-track]:to-emerald-400
            [&::-moz-range-track]:rounded-full
            [&::-moz-range-thumb]:w-5
            [&::-moz-range-thumb]:h-6
            [&::-moz-range-thumb]:rounded-[50%_50%_50%_50%_/_35%_35%_65%_65%]
            [&::-moz-range-thumb]:bg-gradient-to-b
            [&::-moz-range-thumb]:from-emerald-300
            [&::-moz-range-thumb]:to-emerald-500
            [&::-moz-range-thumb]:border-0
            [&::-moz-range-thumb]:cursor-grab"
          data-testid="radius-slider"
        />
      </div>
      
      {/* Current value - larger */}
      <span className="text-emerald-400 font-heading text-sm font-bold min-w-[45px] text-right flex-shrink-0">
        {currentRadius} km
      </span>
    </div>
  );
};

// Compact inline radius control (for filter bars) - LEGACY, kept for compatibility
export const RadiusSliderInline = ({
  radius,
  onRadiusChange,
  enabled,
  onEnableToggle,
  userLocation,
  locationLoading,
  onRequestLocation,
  showLabel = true,
  className = '',
  t // Translation function - optional for backwards compatibility
}) => {
  // Use translation function or fallback to default text
  const getLabel = (key, fallback) => t ? t(key) : fallback;

  const handleEnableClick = async () => {
    if (!userLocation && onRequestLocation) {
      try {
        await onRequestLocation();
        onEnableToggle?.(true);
      } catch (e) {
        // Location denied
      }
    } else {
      onEnableToggle?.(!enabled);
    }
  };

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {/* Toggle button */}
      <div className="flex items-center gap-3">
        {!enabled ? (
          <button
            onClick={handleEnableClick}
            disabled={locationLoading}
            className="h-10 px-4 rounded-xl text-sm font-medium bg-gradient-to-b from-emerald-600 to-emerald-800 text-white flex items-center gap-2 hover:from-emerald-500 hover:to-emerald-700 transition-all disabled:opacity-50"
            data-testid="enable-radius-btn"
          >
            {locationLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Compass className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">{getLabel('map.searchWithin', 'Search within')}</span>
            <span className="sm:hidden">{getLabel('map.radius', 'Radius')}</span>
          </button>
        ) : (
          <button
            onClick={() => onEnableToggle?.(false)}
            className="h-10 px-3 rounded-xl text-sm font-medium bg-zinc-700 text-cream-muted border border-white/10 hover:bg-zinc-600 transition-all"
            data-testid="disable-radius-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Show current radius when enabled */}
        {enabled && userLocation && (
          <span className="text-emerald-400 font-heading text-lg">
            {radius} km
          </span>
        )}
      </div>
    </div>
  );
};

// Nearest club info card component
export const NearestClubCard = ({ clubs, userLocation, radius, onSelectClub, t }) => {
  // Use translation function or fallback to default text
  const getLabel = (key, fallback) => t ? t(key) : fallback;
  
  const nearestClubInfo = useMemo(() => {
    if (!userLocation || !clubs?.length) return null;

    const clubsWithDistance = clubs
      .map(club => ({
        ...club,
        distance: calculateDistance(
          userLocation[0], userLocation[1],
          club.latitude, club.longitude
        ),
        status: getClubStatus(club),
        opensAt: getOpeningTimeToday(club.opening_hours)
      }))
      .filter(club => club.distance <= radius)
      .sort((a, b) => a.distance - b.distance);

    if (clubsWithDistance.length === 0) return null;

    // Find nearest open club
    const openClubs = clubsWithDistance.filter(c => c.status === 'open');
    if (openClubs.length > 0) {
      return {
        club: openClubs[0],
        message: getLabel('map.nearestOpenClub', 'Nearest open club'),
        status: 'open'
      };
    }

    // Find nearest club that opens later today
    const opensLaterClubs = clubsWithDistance.filter(c => c.status === 'opens_later');
    if (opensLaterClubs.length > 0) {
      const nearest = opensLaterClubs[0];
      return {
        club: nearest,
        message: `${getLabel('map.opensAt', 'Opens at')} ${nearest.opensAt}`,
        status: 'opens_later'
      };
    }

    // All clubs are closed
    return {
      club: clubsWithDistance[0],
      message: getLabel('map.noClubsOpenToday', 'None open today'),
      status: 'closed'
    };
  }, [clubs, userLocation, radius, getLabel]);

  if (!nearestClubInfo) return null;

  return (
    <div 
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:scale-[1.02] ${
        nearestClubInfo.status === 'open' 
          ? 'border-green-500/50 bg-green-500/10' 
          : nearestClubInfo.status === 'opens_later'
            ? 'border-yellow-500/50 bg-yellow-500/10'
            : 'border-red-500/30 bg-red-500/5'
      }`}
      onClick={() => onSelectClub?.(nearestClubInfo.club)}
      data-testid="nearest-club-card"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            nearestClubInfo.status === 'open' 
              ? 'bg-green-500' 
              : nearestClubInfo.status === 'opens_later'
                ? 'bg-yellow-500'
                : 'bg-red-500'
          }`}>
            <Navigation className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className={`text-xs font-medium mb-0.5 ${
              nearestClubInfo.status === 'open' 
                ? 'text-green-400' 
                : nearestClubInfo.status === 'opens_later'
                  ? 'text-yellow-400'
                  : 'text-red-400'
            }`}>
              {nearestClubInfo.message}
            </div>
            <div className="font-heading text-lg text-cream">
              {nearestClubInfo.club.name}
            </div>
            <div className="text-cream-muted text-sm">
              {nearestClubInfo.club.distance?.toFixed(1).replace('.', ',')} {getLabel('common.kmAway', 'km away')} • {nearestClubInfo.club.city || nearestClubInfo.club.region}
            </div>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-cream-muted" />
      </div>
    </div>
  );
};

export default {
  RadiusSliderAboveMap,
  RadiusSliderInline,
  PermanentRadiusSlider,
  NearestClubCard,
  useUserLocation,
  useRadiusFilter,
  calculateDistance,
  RADIUS_STEPS,
  DEFAULT_RADIUS,
  DEFAULT_RADIUS_INDEX
};
